import { useTranslation } from 'react-i18next';

interface FooterProps {
  onBookClick: () => void;
}

export default function Footer({ onBookClick }: FooterProps) {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#080808] text-white border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-20 mb-16">
          {/* Brand */}
          <div className="md:col-span-1">
            {/* Logo */}
            <div className="flex items-center gap-2.5 mb-6">
              <div className="w-7 h-7 flex-shrink-0">
                <svg viewBox="0 0 100 100" className="w-full h-full">
                  <defs>
                    <linearGradient id="footerLogoGold" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{ stopColor: '#d97706', stopOpacity: 1 }} />
                      <stop offset="100%" style={{ stopColor: '#f59e0b', stopOpacity: 1 }} />
                    </linearGradient>
                  </defs>
                  <circle cx="35" cy="50" r="25" fill="url(#footerLogoGold)" opacity="0.9" />
                  <circle cx="65" cy="50" r="25" fill="url(#footerLogoGold)" opacity="0.9" />
                  <circle cx="50" cy="50" r="15" fill="#080808" />
                  <circle cx="50" cy="50" r="8" fill="url(#footerLogoGold)" />
                </svg>
              </div>
              <div
                className="text-base font-semibold whitespace-nowrap"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                <span className="text-white">Plexus</span>
                {' '}
                <span className="text-amber-400">Alliance</span>
              </div>
            </div>

            <p
              className="text-white/30 text-sm font-light leading-relaxed mb-8 max-w-xs"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {t('footer.brand.desc')}
            </p>
            <button
              onClick={onBookClick}
              className="flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-black px-6 py-3 font-semibold text-xs tracking-widest uppercase transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {t('footer.cta')}
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>

          {/* Links */}
          <div className="grid grid-cols-2 gap-10 md:col-span-2">
            <div>
              <h4
                className="text-[10px] font-medium tracking-[0.25em] uppercase text-white/20 mb-6"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {t('footer.treatments')}
              </h4>
              <ul className="space-y-3">
                {[
                  { name: t('service.implants.title'), path: '/treatments#dental-implants' },
                  { name: t('service.veneers.title'), path: '/treatments#porcelain-veneers' },
                  { name: t('service.smile.title'), path: '/treatments#smile-makeover' },
                  { name: t('service.allon4.title'), path: '/treatments#all-on-4' },
                  { name: t('service.whitening.title'), path: '/treatments#teeth-whitening' },
                ].map((item, i) => (
                  <li key={i}>
                    <a
                      href={item.path}
                      className="text-white/30 hover:text-amber-400 text-sm font-light transition-colors cursor-pointer"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      {item.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4
                className="text-[10px] font-medium tracking-[0.25em] uppercase text-white/20 mb-6"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                {t('footer.company')}
              </h4>
              <ul className="space-y-3 mb-8">
                {[
                  { name: t('nav.about'), path: '/about' },
                  { name: t('nav.howItWorks'), path: '/#how-it-works' },
                  { name: t('nav.supervision'), path: '/#supervision' },
                  { name: 'Book Treatment', path: '/book-treatment' },
                  { name: 'FAQ', path: '/faq' },
                ].map((item, i) => (
                  <li key={i}>
                    <a
                      href={item.path}
                      className="text-white/30 hover:text-amber-400 text-sm font-light transition-colors cursor-pointer"
                      style={{ fontFamily: "'Inter', sans-serif" }}
                    >
                      {item.name}
                    </a>
                  </li>
                ))}
              </ul>
              <h4
                className="text-[10px] font-medium tracking-[0.25em] uppercase text-white/20 mb-4"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Contact
              </h4>
              <ul className="space-y-2.5">
                <li>
                  <a
                    href="tel:+905316148445"
                    className="text-white/30 hover:text-amber-400 text-sm font-light transition-colors cursor-pointer"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    +90 531 614 8445
                  </a>
                </li>
                <li>
                  <a
                    href="tel:+13059980037"
                    className="text-white/30 hover:text-amber-400 text-sm font-light transition-colors cursor-pointer"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    +1 (305) 998-0037
                  </a>
                </li>
                <li>
                  <a
                    href="mailto:info@plexusalliance.com"
                    className="text-white/30 hover:text-amber-400 text-sm font-light transition-colors cursor-pointer"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    info@plexusalliance.com
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-white/5 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p
            className="text-white/20 text-xs"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            {t('footer.copyright', { year: currentYear })} |{' '}
            <a
              href="https://readdy.ai/?ref=logo"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-amber-400/60 transition-colors cursor-pointer"
            >
              {t('footer.poweredBy')}
            </a>
          </p>
          <div className="flex items-center gap-3">
            {[
              { href: 'https://wa.me/message/OAKDCTBVIML6F1', icon: 'ri-whatsapp-line', label: 'WhatsApp' },
              { href: '#', icon: 'ri-instagram-line', label: 'Instagram' },
              { href: '#', icon: 'ri-facebook-fill', label: 'Facebook' },
              { href: '#', icon: 'ri-linkedin-fill', label: 'LinkedIn' },
            ].map((s, i) => (
              <a
                key={i}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 flex items-center justify-center border border-white/10 hover:border-amber-400/40 text-white/20 hover:text-amber-400 transition-all cursor-pointer"
                aria-label={s.label}
              >
                <i className={`${s.icon} text-sm`}></i>
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
